import { useState } from 'react'
import { Controller, useController, useForm, type Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { Button, DateField, Label, Modal } from '@atrvd/saas-ui'
import { isCalendarDate } from '@dcp/shared'
import { AmountInput } from '@/components/banking/AmountInput'
import { formatAccountNumber } from '@/components/ledgersync/connectionManagement.utils'
import { useSaveCreditCardManualValues } from '@/hooks/useCreditCardManualValues'
import type { CreditCardAvailabilityRow } from '@/lib/db-tenant'
import { CreditCardSourceBadge } from './CreditCardCells'
import {
  creditCardEditDefaults,
  creditCardEditSchema,
  creditCardPatchFromForm,
  formatIntegrationHint,
  integrationDraft,
  INVALID_DATE_ENTRY,
  sameDraftValue,
  type CreditCardEditValues,
  type CreditCardValueField,
  type FollowedFields,
} from './creditCardManualValues'

const FORM_ID = 'edit-credit-card-form'

const LINKED_NOTE =
  "Edited values replace the integration data until you revert them. Next sync won't overwrite them."

const fieldId = (field: keyof CreditCardEditValues) => `${FORM_ID}-${field}`
const errorId = (field: keyof CreditCardEditValues) => `${fieldId(field)}-error`
const hintId = (field: CreditCardValueField) => `${fieldId(field)}-integration`
const revertId = (field: CreditCardValueField) => `${fieldId(field)}-revert`

/**
 * What a value field's `aria-describedby` names: on a linked card the "From
 * integration" hint and, while shown, Revert — so a screen reader announces the
 * integration figure and that it can be put back — then the error, if any.
 */
function valueFieldDescribedBy(
  field: CreditCardValueField,
  { linked, changed, invalid }: { linked: boolean; changed: boolean; invalid: boolean },
): string | undefined {
  const ids = [
    linked && hintId(field),
    linked && changed && revertId(field),
    invalid && errorId(field),
  ].filter((id): id is string => typeof id === 'string')
  return ids.length > 0 ? ids.join(' ') : undefined
}

interface EditCreditCardDialogProps {
  row: CreditCardAvailabilityRow
  onClose: () => void
}

/**
 * "Edit credit card": manual Total Credit Limit, Current Balance (Usage),
 * Payment Due Date, Statement Balance and Note for one card.
 *
 * Mount it only while open. Save sends only the fields the user changed
 * ({@link creditCardPatchFromForm}); with nothing changed it closes without
 * a request. On a linked card each figure also shows the integration's value
 * and, once the draft differs from it, a Revert, which makes the field
 * follow the integration (saved as `null`) until the user edits it again.
 * Amounts owed are typed as positive numbers, 0 or more. Invalid input is
 * flagged on the field as it is typed and blocks Save.
 */
export function EditCreditCardDialog({ row, onClose }: EditCreditCardDialogProps) {
  const save = useSaveCreditCardManualValues()
  const linked = row.source === 'linked'
  const [followed, setFollowed] = useState<FollowedFields>({})
  const setFollowing = (field: CreditCardValueField, following: boolean) =>
    setFollowed((current) => (!!current[field] === following ? current : { ...current, [field]: following }))

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid, dirtyFields },
  } = useForm<CreditCardEditValues>({
    resolver: zodResolver(creditCardEditSchema(row)),
    // `values`, not `defaultValues`: a refetch while the dialog is open (a sync,
    // another save) moves untouched fields to the figures the table now shows.
    // Display only — untouched fields are not sent. RHF resets only when the
    // object differs deeply, not on every render.
    values: creditCardEditDefaults(row),
    resetOptions: {
      keepDirtyValues: true,
      // A kept invalid entry (a half-typed date) keeps its message; without it
      // Save would stay disabled with nothing on screen saying why.
      keepErrors: true,
    },
    // Validate as the user types, so a non-numeric amount is marked at once.
    mode: 'onChange',
  })

  const submit = handleSubmit(async (values) => {
    const changes = creditCardPatchFromForm(values, dirtyFields, followed)
    // Decided on the patch, not on `isDirty`: a `values` reset with
    // `keepDirtyValues` clears `isDirty` but keeps `dirtyFields`.
    if (Object.keys(changes).length === 0) {
      onClose()
      return
    }
    try {
      // `mutateAsync`, not `mutate` with a per-call callback: the refetch may
      // move the card off the current page and unmount this dialog before the
      // save settles, and a per-call callback would then never close it.
      await save.mutateAsync({ bankAccountId: row.id, changes })
      onClose()
    } catch {
      // The hook has already toasted the error; the dialog stays open with the draft.
    }
  })

  const mask = row.account_number ? ` ${formatAccountNumber(row.account_number)}` : ''

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!save.isPending) onClose()
      }}
      title="Edit credit card"
      description={`${row.name}${mask} · ${row.facility_name}`}
      size="lg"
      footer={
        <div className="flex items-center gap-3">
          {linked && <p className="flex-1 text-xs text-muted-foreground">{LINKED_NOTE}</p>}
          <div className="ml-auto flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" form={FORM_ID} disabled={save.isPending || !isValid}>
              {save.isPending && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="space-y-4" noValidate>
        {/* Modal's title takes text only, so the source badge leads the body. */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Source</span>
          <CreditCardSourceBadge source={row.source} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <AmountField
            control={control}
            row={row}
            name="credit_limit"
            label="Total Credit Limit"
            following={!!followed.credit_limit}
            onFollowingChange={(following) => setFollowing('credit_limit', following)}
          />
          <AmountField
            control={control}
            row={row}
            name="balance"
            label="Current Balance (Usage)"
            following={!!followed.balance}
            onFollowingChange={(following) => setFollowing('balance', following)}
          />

          <PaymentDueDateField
            control={control}
            row={row}
            following={!!followed.payment_due_date}
            onFollowingChange={(following) => setFollowing('payment_due_date', following)}
          />

          <AmountField
            control={control}
            row={row}
            name="statement_balance"
            label="Statement Balance"
            following={!!followed.statement_balance}
            onFollowingChange={(following) => setFollowing('statement_balance', following)}
          />

          <div className="col-span-2 space-y-2">
            <Label htmlFor={fieldId('note')}>Note</Label>
            <textarea
              id={fieldId('note')}
              rows={3}
              placeholder="Add a note"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive"
              aria-invalid={!!errors.note}
              aria-describedby={errors.note ? errorId('note') : undefined}
              {...register('note')}
            />
            <FieldError field="note" message={errors.note?.message} />
          </div>
        </div>
      </form>
    </Modal>
  )
}

interface FollowingProps {
  /** The field was put back to the integration with Revert and not edited since. */
  following: boolean
  onFollowingChange: (following: boolean) => void
}

interface AmountFieldProps extends FollowingProps {
  control: Control<CreditCardEditValues>
  row: CreditCardAvailabilityRow
  name: Exclude<CreditCardValueField, 'payment_due_date'>
  label: string
}

/**
 * One amount of the form, with the integration hint and Revert on a linked card.
 *
 * While the field follows the integration it shows the current row's
 * integration figure; its form value is ignored and Save sends `null`, so a
 * refetch that moves the figure cannot leave a stale number behind.
 */
function AmountField({ control, row, name, label, following, onFollowingChange }: AmountFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const integration = integrationDraft(row)[name]
        const linked = row.source === 'linked'
        const shown = following ? integration : field.value
        const changed = !following && !sameDraftValue(field.value, integration)
        return (
          <div className="space-y-2">
            <Label htmlFor={fieldId(name)}>{label}</Label>
            <AmountInput
              id={fieldId(name)}
              // The form holds an empty amount as null (see the schema); AmountInput speaks undefined.
              value={shown ?? undefined}
              onChange={(value) => {
                onFollowingChange(false)
                field.onChange(value ?? null)
              }}
              onBlur={field.onBlur}
              placeholder="0.00"
              className="aria-[invalid=true]:border-destructive"
              aria-invalid={!!fieldState.error}
              aria-describedby={valueFieldDescribedBy(name, { linked, changed, invalid: !!fieldState.error })}
            />
            {linked && (
              <IntegrationHint
                row={row}
                field={name}
                label={label}
                changed={changed}
                onRevert={() => {
                  field.onChange(null)
                  onFollowingChange(true)
                }}
              />
            )}
            <FieldError field={name} message={fieldState.error?.message} />
          </div>
        )
      }}
    />
  )
}

interface PaymentDueDateFieldProps extends FollowingProps {
  control: Control<CreditCardEditValues>
  row: CreditCardAvailabilityRow
}

/**
 * Payment Due Date, with the integration hint and Revert on a linked card.
 *
 * Text that is not a date yet holds {@link INVALID_DATE_ENTRY} in the form, so
 * it shows an error and blocks Save instead of reading as a cleared date.
 * `DateField` cannot show such text once focus leaves — it redraws the last
 * date it reported — so on blur the field goes back to the last value that was
 * not such text (a date, or empty), as `DateField`'s own snap-back does,
 * rather than leaving an error beside a blank input — or, if the field followed
 * the integration before that text, back to following it.
 *
 * While it follows the integration it shows the current row's integration
 * date; its form value is ignored and Save sends `null`, as in {@link AmountField}.
 */
function PaymentDueDateField({ control, row, following, onFollowingChange }: PaymentDueDateFieldProps) {
  const { field, fieldState } = useController({ control, name: 'payment_due_date' })
  /**
   * What blur goes back to. Adjusted during render rather than in
   * `onEntryChange`, so a value set from outside (Revert, a refetch) counts too.
   */
  const [lastComplete, setLastComplete] = useState({ value: field.value, following })
  if (
    field.value !== INVALID_DATE_ENTRY &&
    (field.value !== lastComplete.value || following !== lastComplete.following)
  ) {
    setLastComplete({ value: field.value, following })
  }
  const integration = integrationDraft(row).payment_due_date
  const linked = row.source === 'linked'
  const shown = following ? integration : field.value
  const changed = !following && !sameDraftValue(field.value, integration)

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldId('payment_due_date')}>Payment Due Date</Label>
      <DateField
        ref={field.ref}
        id={fieldId('payment_due_date')}
        value={isCalendarDate(shown) ? shown : ''}
        onValueChange={(value) => {
          onFollowingChange(false)
          field.onChange(value)
        }}
        onEntryChange={(entry) => {
          if (entry.status === 'incomplete') field.onChange(INVALID_DATE_ENTRY)
        }}
        onBlur={() => {
          if (field.value === INVALID_DATE_ENTRY) {
            field.onChange(lastComplete.value)
            onFollowingChange(lastComplete.following)
          }
          field.onBlur()
        }}
        className="aria-[invalid=true]:border-destructive"
        aria-invalid={!!fieldState.error}
        aria-describedby={valueFieldDescribedBy('payment_due_date', {
          linked,
          changed,
          invalid: !!fieldState.error,
        })}
      />
      {linked && (
        <IntegrationHint
          row={row}
          field="payment_due_date"
          label="Payment Due Date"
          changed={changed}
          onRevert={() => {
            field.onChange('')
            onFollowingChange(true)
          }}
        />
      )}
      {/* The line is reserved even without an error: the blur fallback clears
          the error as a press on Revert begins, and a shrinking (centred)
          dialog would move Revert out from under the pointer before the click. */}
      <FieldError field="payment_due_date" message={fieldState.error?.message} reserveLine />
    </div>
  )
}

interface FieldErrorProps {
  field: keyof CreditCardEditValues
  message: string | undefined
  /** Keep the line's height while there is no error, so the layout does not move when one clears. */
  reserveLine?: boolean
}

function FieldError({ field, message, reserveLine = false }: FieldErrorProps) {
  if (!message) return reserveLine ? <p aria-hidden="true" className="min-h-5 text-sm" /> : null
  return (
    <p id={errorId(field)} className="min-h-5 text-sm text-destructive">
      {message}
    </p>
  )
}

interface IntegrationHintProps {
  row: CreditCardAvailabilityRow
  field: CreditCardValueField
  label: string
  /** The draft differs from the integration's value. */
  changed: boolean
  onRevert: () => void
}

/**
 * "From integration: <value>" in form units, and Revert while the draft differs
 * from it. Both carry ids the field's `aria-describedby` names.
 */
function IntegrationHint({ row, field, label, changed, onRevert }: IntegrationHintProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span id={hintId(field)}>
        From integration:{' '}
        <span className="font-semibold tabular-nums text-foreground">
          {formatIntegrationHint(row, field)}
        </span>
      </span>
      {changed && (
        <Button
          id={revertId(field)}
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          aria-label={`Revert ${label}`}
          aria-controls={fieldId(field)}
          onClick={onRevert}
        >
          Revert
        </Button>
      )}
    </div>
  )
}
